<template>
  <div class="w-full h-full bg-white flex flex-col">
    <app-header class="w-full settings-markets-header">
      <div class="flex items-center justify-between w-full gap-4 py-8 px-14">
        <div class="flex flex-col gap-1 w-7/12">
          <h2 class="font-semibold">Markets</h2>
          <span>Markets group your bots and knowledge bases by language and region. Attach a market to a bot variant or a knowledge base to scope it.</span>
        </div>

        <div class="w-4/12">
          <search-input v-model="searchQuery" variant="search-variables"/>
        </div>

        <busy-button id="newMarketButton"
                     icon="add"
                     icon-size="1.5rem"
                     variant="primary"
                     @click="startCreate"
        >
          <span class="my-auto ml-1">Add</span>
        </busy-button>
      </div>
    </app-header>

    <div class="px-14 py-8 w-full h-full overflow-y-auto">
      <with-loader id="settings-markets-table-loader"
                   :loading="isLoading"
                   class="rounded-lg outline-soft border-soft"
                   use-as-overlay
      >
        <table class="w-full rounded-l border-collapse">
          <thead class="bg-gray text-white rounded-lg">
            <tr class="flex w-full py-2">
              <th class="w-3/12 text-base font-normal pl-7">Name</th>
              <th class="w-2/12 text-base font-normal">Language</th>
              <th class="w-4/12 text-base font-normal">Used by</th>
              <th class="w-3/12 text-base font-normal"></th>
            </tr>
          </thead>
          <tbody>
            <div class="divide-x-0 divide-y divide-solid divide-soft">
              <template v-for="market in filteredMarkets">
                <tr :key="market.id" class="flex w-full items-center py-2">
                  <td class="w-3/12 pl-7 pr-2">
                    <input-with-error-message v-if="renamingMarketId === market.id"
                                              :id="`market-name-${market.id}`"
                                              :value.sync="renameValue"
                                              error-message="Name cannot be empty"
                                              :show-cancel-button="true"
                                              class="h-10"
                                              @escape="cancelRename"
                                              @submit="submitRename(market)"
                    />
                    <div v-else
                         class="w-full truncate border border-transparent rounded border-solid cursor-text hover:border-gray-border"
                         @click="startRename(market)"
                    >
                      {{ market.name }}
                    </div>
                  </td>
                  <td class="w-2/12">{{ languageLabel(market.language) }}</td>
                  <td class="w-4/12">
                    <a v-if="usageFor(market.id).total > 0" class="cursor-pointer text-primary" @click="toggleExpanded(market.id)">
                      {{ usageSummary(market.id) }}
                    </a>
                    <span v-else class="text-gray">Not used</span>
                  </td>
                  <td class="w-3/12 flex items-center justify-end gap-1 pr-7">
                    <vx-tooltip v-if="usageFor(market.id).total > 0"
                                :text="deleteDisabledReason(market.id)"
                                position="bottom"
                                class="flex items-center justify-center h-10 w-10 rounded-full text-gray bg-soft opacity-50 cursor-not-allowed"
                    >
                      <delete-icon class="h-5 w-5"/>
                    </vx-tooltip>
                    <vx-tooltip v-else
                                text="Delete market"
                                position="bottom"
                                class="flex items-center justify-center h-10 w-10 rounded-full cursor-pointer text-focus-error bg-focus-error-light hover:opacity-80"
                                @click.native="requestDelete(market)"
                    >
                      <delete-icon class="h-5 w-5"/>
                    </vx-tooltip>
                  </td>
                </tr>
                <tr v-if="expandedMarketId === market.id" :key="`${market.id}-detail`" class="flex w-full bg-gray-light">
                  <td class="w-full py-3 px-7 text-gray-dark">
                    <div v-if="usageFor(market.id).bots.length">
                      <strong>Bots:</strong> {{ usageFor(market.id).bots.map(bot => multiBotNameFor(bot)).join(', ') }}
                    </div>
                    <div v-if="usageFor(market.id).indexes.length" class="mt-1">
                      <strong>Knowledge bases:</strong> {{ usageFor(market.id).indexes.map(index => index.name).join(', ') }}
                    </div>
                  </td>
                </tr>
              </template>
            </div>
            <tr v-if="!filteredMarkets.length" class="flex w-full">
              <td class="py-4 px-7 text-gray">{{ markets.length ? 'No markets match your search.' : 'No markets yet.' }}</td>
            </tr>
          </tbody>
        </table>
      </with-loader>
    </div>

    <base-dialog v-if="isFormShown"
                 :value="isFormShown"
                 title="New market"
                 @cancel="closeForm"
    >
      <template #prompt-input>
        <base-label id="marketName" label="Name" class="mt-2"/>
        <base-input id="marketName" name="marketName" variant="editor" class="w-full h-10 mt-1" v-model.trim="form.name"/>

        <base-label id="marketLanguage" label="Language" class="mt-4"/>
        <queryable-select id="marketLanguage" :value="form.language" :options="languageOptions" class="w-full h-10 mt-1" @input="form.language = $event"/>
      </template>

      <template #confirm-button>
        <busy-button id="saveMarketButton" variant="primary" class="w-full ml-4" :is-loading="isSaving" :is-disabled="!isFormValid || isSaving" @click="save">
          Save
        </busy-button>
      </template>
    </base-dialog>

    <delete-dialog v-if="isDeleteDialogShown"
                   :is-shown="isDeleteDialogShown"
                   :item="marketToDelete"
                   :dialog-type="'confirm'"
                   :delete-title="'Delete Market?'"
                   :delete-description="deleteDescription"
                   :delete-confirm-text="'Delete Market'"
                   @cancel="cancelDelete"
                   @delete="deleteMarket"
    />
  </div>
</template>

<script>
import AppHeader from '@/components/common/AppHeader.vue'
import SearchInput from '@/components/base-components/SearchInput.vue'
import BusyButton from '@/components/common/BusyButton'
import WithLoader from '@/components/common/WithLoader.vue'
import QueryableSelect from '@/components/common/QueryableSelect.vue'
import DeleteDialog from '@/components/common/DeleteDialog.vue'
import InputWithErrorMessage from '@/components/common/InputWithErrorMessage.vue'
import DeleteIcon from '@/assets/images/icons/DeleteIcon.vue'
import marketService from '@/services/amio/market.service'
import indexesService from '@/services/amio/indexes.service'
import marketModule from '@/store/modules/market.module'
import botModule from '@/store/modules/bot.module'
import busyButtonsModule from '@/store/modules/busy-buttons.module'
import { BOT_LANGUAGES } from '@/utils/bot.constants'
import { BUSY_BUTTONS } from '@/utils/busy-buttons.constants'
import { multiBotNameForBot } from '@/utils/market.utils'
import { ensureBotAndMarketLists } from '@/utils/bot-market.utils'
import toaster from '@/utils/toaster'

export default {
  name: 'SettingsMarkets',
  components: { AppHeader, SearchInput, BusyButton, WithLoader, QueryableSelect, DeleteDialog, InputWithErrorMessage, DeleteIcon },

  data() {
    return {
      markets: [],
      indexes: [],
      isLoading: false,
      searchQuery: '',
      expandedMarketId: null,
      isFormShown: false,
      isSaving: false,
      form: { name: '', language: 'en' },
      renamingMarketId: null,
      renameValue: '',
      marketToDelete: null,
      isDeleteDialogShown: false
    }
  },

  computed: {
    bots() {
      return botModule.getBotList()
    },
    filteredMarkets() {
      if (!this.searchQuery.trim()) return this.markets

      const term = this.searchQuery.toLowerCase().trim()
      return this.markets.filter(market =>
        market.name.toLowerCase().includes(term) || this.languageLabel(market.language).toLowerCase().includes(term)
      )
    },
    usageByMarketId() {
      const map = {}
      this.markets.forEach(market => {
        const bots = this.bots.filter(bot => bot.market_id === market.id)
        const indexes = this.indexes.filter(index => index.market_id === market.id)
        map[market.id] = { bots, indexes, total: bots.length + indexes.length }
      })
      return map
    },
    languageOptions() {
      return BOT_LANGUAGES
    },
    isFormValid() {
      return Boolean(this.form.name.trim()) && Boolean(this.form.language)
    },
    deleteDescription() {
      return this.marketToDelete ? `This will permanently delete the market "${this.marketToDelete.name}". This action cannot be undone.` : ''
    }
  },

  methods: {
    languageLabel(code) {
      return BOT_LANGUAGES.find(lang => lang.value === code)?.text || code
    },
    // Never show a bot variant's own name here — it's redundant with (and can drift from) the
    // market name. Show the multibot (group) name instead.
    multiBotNameFor(bot) {
      return multiBotNameForBot(bot)
    },
    usageFor(marketId) {
      return this.usageByMarketId[marketId] || { bots: [], indexes: [], total: 0 }
    },
    usageSummary(marketId) {
      const { bots, indexes } = this.usageFor(marketId)
      const parts = []
      if (bots.length) parts.push(`${bots.length} bot${bots.length === 1 ? '' : 's'}`)
      if (indexes.length) parts.push(`${indexes.length} knowledge base${indexes.length === 1 ? '' : 's'}`)
      return parts.join(' · ')
    },
    deleteDisabledReason(marketId) {
      const { bots, indexes } = this.usageFor(marketId)
      const parts = []
      if (bots.length) parts.push(`${bots.length} bot${bots.length === 1 ? '' : 's'}`)
      if (indexes.length) parts.push(`${indexes.length} knowledge base${indexes.length === 1 ? '' : 's'}`)
      return `Used by ${parts.join(' and ')} — remove them first`
    },
    toggleExpanded(marketId) {
      this.expandedMarketId = this.expandedMarketId === marketId ? null : marketId
    },
    startCreate() {
      this.form = { name: '', language: 'en' }
      this.isFormShown = true
    },
    closeForm() {
      this.isFormShown = false
    },
    async save() {
      if (!this.isFormValid || this.isSaving) return
      this.isSaving = true
      try {
        const created = await marketService.create(this.form.language, this.form.name.trim())
        this.markets.push(created)
        marketModule.setMarketList(this.markets)
        this.isFormShown = false
        toaster.success('Market created')
      } catch (err) {
        toaster.error(err.message || err)
      } finally {
        this.isSaving = false
      }
    },
    startRename(market) {
      this.renamingMarketId = market.id
      this.renameValue = market.name
    },
    cancelRename() {
      this.renamingMarketId = null
      this.renameValue = ''
    },
    // language is immutable once a market exists — it's only read at variant-creation time to
    // seed a NEW bot's language, so changing it later wouldn't affect anything already using
    // this market and would just be misleading. Rename only ever touches the name.
    async submitRename(market) {
      const trimmed = this.renameValue.trim()
      if (!trimmed || trimmed === market.name) {
        this.cancelRename()
        return
      }
      try {
        const updated = await marketService.update(market.id, { name: trimmed })
        const index = this.markets.findIndex(m => m.id === market.id)
        if (index >= 0) this.markets.splice(index, 1, updated)
        marketModule.setMarketList(this.markets)
        toaster.success('Market renamed')
      } catch (err) {
        toaster.error(err.message || err)
      } finally {
        this.cancelRename()
      }
    },
    requestDelete(market) {
      this.marketToDelete = market
      this.isDeleteDialogShown = true
    },
    cancelDelete() {
      this.marketToDelete = null
      this.isDeleteDialogShown = false
    },
    async deleteMarket() {
      if (!this.marketToDelete) return
      busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, true)
      try {
        await marketService.delete(this.marketToDelete.id)
        this.markets = this.markets.filter(market => market.id !== this.marketToDelete.id)
        marketModule.setMarketList(this.markets)
        toaster.success('Market deleted')
      } catch (err) {
        toaster.error(err.message || err)
      } finally {
        this.marketToDelete = null
        this.isDeleteDialogShown = false
        busyButtonsModule.setButtonBusy(BUSY_BUTTONS.ITEM_DELETE, false)
      }
    },
    async loadData() {
      const [markets, indexes] = await Promise.all([
        marketService.list(),
        indexesService.list(),
        // bots aren't fetched by this page directly — ensure the shared store list is loaded
        // (a cold navigation to /settings/markets otherwise races with usage showing "Not used")
        ensureBotAndMarketLists()
      ])
      this.markets = markets
      this.indexes = indexes
      marketModule.setMarketList(markets)
    }
  },

  async mounted() {
    this.isLoading = true
    try {
      await this.loadData()
    } catch (err) {
      toaster.error(err.message || err)
    } finally {
      this.isLoading = false
    }
  }
}
</script>

<style lang="scss">
// AppHeader hard-codes a 100px height (scoped), too short for this page's two-line
// description — matches VariablesHeader.vue's override for the same reason.
.settings-markets-header {
  height: unset !important;
}

#newMarketButton {
  button {
    @apply flex flex-row-reverse;
  }
}
</style>
